import styled, { css } from 'styled-components'
import { device } from '@/common/constants/device'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { hideTextWithDots } from '@/components/common/BaseMixins'
import BaseLink from '@/components/common/BaseLink'

export const CreativesCustomScrollbar = css`
  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--tertiary);
    border-radius: 110px;
  }

  .scrollbar-width {
    scrollbar-width: thin;
  }

  .scrollbar-color {
    scrollbar-color: var(--tertiary) transparent;
  }
`

export const CreativesLinkStyle = css`
  text-decoration: none;
  color: var(--purple);
  border-bottom: 1px solid var(--purple);
  transition: border-color 0.26s ease-in-out;
  cursor: pointer;

  &:hover {
    border-color: transparent;
  }
`

export const CreativesLoadingTextWrap = styled.div`
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(var(--natural-black-rgb), 0.1);
  text-align: center;
  padding: 8px 12px;
  border-radius: 8px;
  z-index: 50;

  @media ${device.tablet} {
    padding: 14px 18px;
    padding-right: 20px;
  }
`

export const CreativesLoadingText = styled.span`
  font-size: 16px;
  line-height: 1.2em;
  color: var(--natural-black);

  @media ${device.tablet} {
    font-size: 18px;
  }
`

export const CreativesLoadingTextDots = styled.span`
  position: absolute;
  padding-right: 6px;
  display: inline-block;
  overflow: hidden;
  vertical-align: bottom;
  animation: extend 3s steps(3, end) infinite;

  &::after {
    content: '...';
  }
`

export const CreativesButton = styled(Button)`
  padding: 9px 12px;
  font-size: 16px;
  line-height: 1.2em;
  font-weight: 500;
  border-radius: 12px;
  box-shadow: 0px 4px 4px 0px rgba(57, 62, 75, 0.08);

  &.btn-success {
    padding: 9px 12px;
    border-radius: 12px;
    height: 38px;
  }

  &:disabled {
    border-color: transparent;
  }
`

export const CreativesInput = styled(Input)`
  width: 100%;

  input {
    padding: 14px 24px;
    border-radius: 12px;
    width: 100%;
  }
`

export const CreativesBlock = styled.div`
  display: none;

  @media ${device.laptopM} {
    width: 445px;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 0px 0px 0px rgba(27, 27, 68, 0.08),
      -1px 6px 13px 0px rgba(27, 27, 68, 0.08),
      -2px 24px 24px 0px rgba(27, 27, 68, 0.07),
      -5px 54px 32px 0px rgba(27, 27, 68, 0.04),
      -9px 96px 38px 0px rgba(27, 27, 68, 0.01),
      -13px 149px 42px 0px rgba(27, 27, 68, 0);
    background-color: var(--white);
  }
`

export const CreativesBlockTop = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  padding: 12px 16px;
  border-bottom: 2px solid var(--stroke);

  @media ${device.tablet} {
    padding: 24px;
  }
`

export const CreativesBlockTitle = styled.div`
  font-size: 18px;
  line-height: 1.4em;
  font-weight: 600;
  display: flex;
  align-items: center;

  @media ${device.tablet} {
    font-size: 20px;
    line-height: 1.2em;
    height: 39px;
  }
`

export const CreativesModalTitle = styled.div`
  font-size: 18px;
  line-height: 1.4em;
  font-weight: 600;
  color: var(--black-russian);

  @media ${device.tablet} {
    font-size: 20px;
    line-height: 1.2em;
  }
`

export const CreativesText = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.2em;
  font-weight: 500;
  color: var(--secondary);
`

export const CreativesBlockContent = styled.div`
  flex-grow: 1;
  padding: 24px;
  overflow-y: auto;
  ${CreativesCustomScrollbar};
`

export const CreativesBlockBottom = styled.div`
  padding: 12px 16px;
  border-top: 2px solid var(--stroke);

  @media ${device.tablet} {
    padding: 24px;
  }
`

export const CreativesContent = styled.div`
  max-width: 1664px;
  margin: 0 auto;
  width: 100%;
`

export const CreativesRight = styled.div`
  width: fit-content;
  position: sticky;
  top: 62px;
  overflow-y: auto;
  max-height: calc(100vh - 130px);
  min-height: 600px;
`

export const CreativesBottomModal = styled.div`
  display: flex;
  justify-content: flex-end;
`

export const CreativesOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  opacity: 0;
  transition: opacity 0.26s ease-in-out;
`

export const CreativesLink = styled.a`
  font-weight: 600;
  color: var(--purple);
  ${hideTextWithDots};
  width: fit-content;
  max-width: 100%;
  transition: color 0.26s ease-in-out;
  cursor: pointer;

  &:hover {
    color: var(--yellow);
  }
`

export const CreativesPlaceholderLink = styled(BaseLink)`
  padding: 9px 12px;
  font-size: 16px;
  line-height: 1.2em;
  font-weight: 500;
  border-radius: 12px;
  box-shadow: 0px 4px 4px 0px rgba(57, 62, 75, 0.08);
  color: var(--black);
  background-color: var(--yellow);

  &:hover {
    background-color: #fae21a;
    border-color: #3f3f49;
  }

  &:active {
    background-color: #ededee;
    border-color: #ededee;
  }
`

export const CreativesInnerContent = styled(CreativesContent)`
  overflow: hidden;

  @media ${device.tablet} {
    margin-top: 20px;
  }

  .creative-demo-placeholder {
    .placeholder-content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .placeholder-link {
      display: inline-flex;
      margin-top: 12px;
    }
  }
`

export const CreativesFavoriteTopWrap = styled.div`
  padding: 12px 12px;
  border-bottom: 1px solid var(--stroke);
  margin-bottom: 16px;

  @media ${device.tablet} {
    padding: 16px 0;
    padding-left: 24px;
    margin-bottom: 16px;
  }
`

export const CartsHeadImage = styled.div`
  position: relative;
  width: 32px;
  height: 32px;

  @media ${device.tablet} {
    width: 38px;
    height: 38px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    flex-shrink: 0;
    border-radius: 50%;
  }
`

export const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`

export const CenteredBlock = styled.div`
  display: flex;
  justify-content: center;
`

export const hideMobile = css`
  display: none;

  @media ${device.tablet} {
    display: ${({ display }) => display ?? 'block'};
  }
`

export const hideTablet = css`
  @media ${device.tablet} {
    display: none;
  }
`

export const SearchHyphenPlaceholder = styled.span`
  display: inline-flex;
  width: 10px;
  height: 2px;
  background-color: var(--stroke);
`

export const CreativesActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    column-gap: 6px;
    border: none;
    background-color: transparent;
`
